import type { OrganisationTree } from 'api/organisations';
import type { FarmOption } from 'farmContext';
import type { AccountFarmRole, AccountInfoData } from 'types';

export type FarmHierarchyInfo = {
  organisationId?: number;
  organisationName?: string;
  memberRoleName?: string;
  companyId?: number;
  companyName?: string;
};

const normalizeId = (value: string | number | null | undefined) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value).trim();
};

export const findFarmHierarchy = (
  organisations: OrganisationTree[] | undefined,
  farmId: string | number | null | undefined,
): FarmHierarchyInfo | null => {
  const targetFarmId = normalizeId(farmId);
  if (!targetFarmId || !organisations?.length) {
    return null;
  }

  for (const org of organisations) {
    const orgFarm = (org.farms ?? []).find((f) => normalizeId(f.id) === targetFarmId);
    if (orgFarm) {
      return {
        organisationId: org.id,
        // Users see customer number only — never internal organisation names.
        organisationName: org.customerNumber,
        memberRoleName: org.memberRoleName,
      };
    }
    for (const company of org.companies ?? []) {
      const farm = (company.farms ?? []).find((f) => normalizeId(f.id) === targetFarmId);
      if (farm) {
        return {
          organisationId: org.id,
          organisationName: org.customerNumber,
          memberRoleName: org.memberRoleName,
          companyId: company.id,
          companyName: company.name,
        };
      }
    }
  }

  return null;
};

export const findFarmInOrganisations = (
  organisations: OrganisationTree[] | undefined,
  farmId: string | number | null | undefined,
) => findFarmHierarchy(organisations, farmId) !== null;

/** Active companies under the organisation that owns this farm (trial billing must use these). */
export const getBillingCompaniesForFarm = (
  organisations: OrganisationTree[] | undefined,
  farmId: string | number | null | undefined,
) => {
  const hierarchy = findFarmHierarchy(organisations, farmId);
  if (!hierarchy?.organisationId) {
    return [];
  }

  const organisation = organisations?.find((org) => org.id === hierarchy.organisationId);
  return (organisation?.companies ?? []).filter((company) => company.active !== false);
};

export const getOrganisationFarmOptions = (organisations?: OrganisationTree[]): FarmOption[] => {
  const options: FarmOption[] = [];
  let index = 0;

  for (const org of organisations ?? []) {
    for (const farm of org.farms ?? []) {
      const farmId = normalizeId(farm.id);
      if (!farmId) {
        continue;
      }
      const label = normalizeId(farm.farmName) || farmId;
      if (options.some((option) => option.farmId === farmId)) {
        continue;
      }
      options.push({
        farmId,
        farmIdCandidates: [farmId],
        farmName: label,
        label,
        value: farmId,
      });
      index += 1;
    }
    for (const company of org.companies ?? []) {
      for (const farm of company.farms ?? []) {
        const farmId = normalizeId(farm.id);
        if (!farmId) {
          continue;
        }

        const label =
          normalizeId(farm.farmName) ||
          normalizeId(company.name) ||
          farmId;

        if (options.some((option) => option.farmId === farmId)) {
          continue;
        }

        options.push({
          farmId,
          farmIdCandidates: [farmId],
          farmName: label,
          label,
          orgNumber: normalizeId(company.companyRegistration) || undefined,
          value: farmId,
        });
        index += 1;
      }
    }
  }

  return options.sort((left, right) => left.label.localeCompare(right.label, 'sv', { sensitivity: 'base' }));
};

export const getFarmRoleForAccount = (
  accountInfo: AccountInfoData | undefined,
  farmId: string | number | null | undefined,
): AccountFarmRole | undefined => {
  const targetFarmId = normalizeId(farmId);
  if (!targetFarmId || !accountInfo?.userFarmRoles?.length) {
    return undefined;
  }

  return accountInfo.userFarmRoles.find((role) => normalizeId(role.farmId) === targetFarmId);
};
